<template>
  <div class="employees-page">

    <!-- ─── Hero Header ─────────────────────────────────────────────── -->
    <div class="emp-hero">
      <div class="emp-hero__left">
        <div class="emp-hero__icon-wrap">
          <feather-icon
            icon="UsersIcon"
            size="28"
            class="emp-hero__icon"
          />
        </div>
        <div>
          <h2 class="emp-hero__title">
            New Employees
          </h2>
          <p class="emp-hero__subtitle">
            Manage and review all new company employees
          </p>
        </div>
      </div>
    </div>

    <!-- Gradient accent (replaces stats bar, matches card palette) -->
    <div
      class="emp-hero-accent"
      aria-hidden="true"
    />

    <!-- ─── Filters Row ──────────────────────────────────────────────── -->
    <b-card
      class="emp-filters-card"
      no-body
    >
      <div class="emp-filters">
        <!-- Search -->
        <div class="emp-search-wrap">
          <feather-icon
            icon="SearchIcon"
            size="15"
            class="emp-search-icon"
          />
          <input
            v-model="searchQuery"
            class="emp-search-input"
            type="text"
            placeholder="Search by name, email, or job title…"
          >
          <button
            v-if="searchQuery"
            class="emp-search-clear"
            @click="searchQuery = ''"
          >
            <feather-icon
              icon="XIcon"
              size="14"
            />
          </button>
        </div>

        <!-- Branch Filter -->
        <div class="emp-branch-filter">
          <feather-icon
            icon="FilterIcon"
            size="14"
            class="emp-branch-filter__icon"
          />
          <select
            v-model="selectedBranch"
            class="emp-branch-select"
          >
            <option value="">
              All Branches
            </option>
            <option
              v-for="branch in uniqueBranches"
              :key="branch"
              :value="branch"
            >
              {{ branch }}
            </option>
          </select>
        </div>

        <!-- Refresh -->
        <button
          class="emp-refresh-btn"
          :disabled="dataLoad"
          @click="fetchEmployees"
        >
          <feather-icon
            icon="RefreshCwIcon"
            size="15"
            :class="{ 'spin-icon': dataLoad }"
          />
          <span>Refresh</span>
        </button>
      </div>
    </b-card>

    <!-- ─── Loading ──────────────────────────────────────────────────── -->
    <div
      v-if="dataLoad"
      class="emp-loading"
    >
      <div class="emp-loading__spinner" />
      <p class="emp-loading__text">
        Loading employees…
      </p>
    </div>

    <!-- ─── Empty State ──────────────────────────────────────────────── -->
    <div
      v-else-if="filteredEmployees.length === 0"
      class="emp-empty"
    >
      <feather-icon
        icon="UserXIcon"
        size="56"
        class="emp-empty__icon"
      />
      <h4 class="emp-empty__title">
        No employees found
      </h4>
      <p class="emp-empty__sub">
        Try adjusting your search or filter criteria
      </p>
      <button
        class="emp-empty__btn"
        @click="clearFilters"
      >
        Clear Filters
      </button>
    </div>

    <!-- ─── Cards Grid ───────────────────────────────────────────────── -->
    <template v-else>
      <div class="emp-grid">
        <div
          v-for="emp in paginatedEmployees"
          :key="emp.id"
          class="emp-card"
        >
          <!-- Gradient top banner -->
          <div
            class="emp-card__top"
            :style="{ background: getBranchGradient(emp.branch) }"
          />

          <!-- Avatar -->
          <div class="emp-card__avatar-wrap">
            <img
              :src="emp.photo || defaultAvatar"
              class="emp-card__avatar"
              :alt="emp.name"
              @error="handleImgError"
            >
          </div>

          <!-- Body -->
          <div class="emp-card__body">
            <h5 class="emp-card__name">
              {{ emp.name }}
            </h5>
            <p class="emp-card__job">
              <feather-icon
                icon="BriefcaseIcon"
                size="12"
              />
              {{ emp.job_title }}
            </p>

            <div
              class="emp-card__branch-badge"
              :style="{ background: getBranchColor(emp.branch) }"
            >
              <feather-icon
                icon="MapPinIcon"
                size="11"
              />
              {{ emp.branch }}
            </div>

            <div class="emp-card__details">
              <div class="emp-card__detail-line">
                <span
                  class="emp-card__detail-icon emp-card__detail-icon--mail"
                  aria-hidden="true"
                >
                  <feather-icon
                    icon="MailIcon"
                    size="14"
                  />
                </span>
                <a
                  :href="`mailto:${emp.email}`"
                  class="emp-card__detail-link"
                >
                  {{ emp.email }}
                </a>
              </div>
              <div class="emp-card__detail-line">
                <span
                  class="emp-card__detail-icon emp-card__detail-icon--phone"
                  aria-hidden="true"
                >
                  <feather-icon
                    icon="PhoneIcon"
                    size="14"
                  />
                </span>
                <a
                  :href="`tel:${emp.mobile}`"
                  class="emp-card__detail-link"
                >
                  {{ emp.mobile }}
                </a>
              </div>
              <div
                v-if="emp.linked_in && isUrl(emp.linked_in)"
                class="emp-card__detail-line"
              >
                <span
                  class="emp-card__detail-icon emp-card__detail-icon--linkedin"
                  aria-hidden="true"
                >
                  <feather-icon
                    icon="LinkedinIcon"
                    size="14"
                  />
                </span>
                <a
                  :href="emp.linked_in"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="emp-card__detail-link emp-card__detail-link--linkedin"
                >
                  LinkedIn Profile
                </a>
              </div>
            </div>
          </div>

          <!-- Footer: same actions as previous SweetAlert (approve / disapprove) -->
          <div class="emp-card__footer emp-card__footer-actions">
            <button
              type="button"
              class="emp-card__action-btn emp-card__action-btn--approve"
              @click="goToApproveEmployee(emp.id, emp.ofice_id)"
            >
              <feather-icon
                icon="CheckCircleIcon"
                size="14"
              />
              Approve
            </button>
            <button
              type="button"
              class="emp-card__action-btn emp-card__action-btn--disapprove"
              @click="disapproveEmployee(emp.id)"
            >
              <feather-icon
                icon="XCircleIcon"
                size="14"
              />
              Disapprove
            </button>
          </div>
        </div>
      </div>

      <!-- ─── Pagination ────────────────────────────────────────────── -->
      <div class="emp-pagination">
        <button
          class="emp-page-btn"
          :disabled="currentPage === 1"
          @click="currentPage--"
        >
          <feather-icon
            icon="ChevronLeftIcon"
            size="16"
          />
        </button>

        <button
          v-for="p in totalPages"
          :key="p"
          class="emp-page-btn emp-page-btn--num"
          :class="{ 'emp-page-btn--active': currentPage === p }"
          @click="currentPage = p"
        >
          {{ p }}
        </button>

        <button
          class="emp-page-btn"
          :disabled="currentPage === totalPages"
          @click="currentPage++"
        >
          <feather-icon
            icon="ChevronRightIcon"
            size="16"
          />
        </button>

        <span class="emp-pagination__info">
          Showing {{ paginatedEmployees.length }} of {{ filteredEmployees.length }}
        </span>
      </div>
    </template>

  </div>
</template>

<script>
import axios from 'axios'
import decryptData from '@/store/auth/decryptData'

const BRANCH_COLORS = [
  '#4361ee', '#7209b7', '#2d9cdb', '#f4a261', '#e76f51',
  '#06d6a0', '#118ab2', '#ef476f', '#ffd166', '#073b4c',
]

const BRANCH_GRADIENTS = [
  'linear-gradient(135deg,#4361ee,#4cc9f0)',
  'linear-gradient(135deg,#7209b7,#b5179e)',
  'linear-gradient(135deg,#2d9cdb,#56cfe1)',
  'linear-gradient(135deg,#f4a261,#e76f51)',
  'linear-gradient(135deg,#06d6a0,#118ab2)',
  'linear-gradient(135deg,#ef476f,#ffd166)',
  'linear-gradient(135deg,#073b4c,#118ab2)',
  'linear-gradient(135deg,#3a86ff,#8338ec)',
  'linear-gradient(135deg,#fb5607,#ffbe0b)',
  'linear-gradient(135deg,#8ecae6,#219ebc)',
]

export default {
  data() {
    return {
      allEmployees: [],
      dataLoad: false,
      searchQuery: '',
      selectedBranch: '',
      currentPage: 1,
      perPage: 12,
      filter: {},
      branchColorMap: {},
      defaultAvatar: 'https://ui-avatars.com/api/?name=Employee&background=4361ee&color=fff&size=128',
    }
  },

  computed: {
    apiUrl() {
      let base = 'employees?'
      if (!Object.values(this.filter).length) return base.slice(0, -1)
      Object.entries(this.filter).forEach(([key, value]) => {
        if (key && value) base += `${key}=${value}&`
      })
      return base.slice(0, -1)
    },

    uniqueBranches() {
      return [...new Set(this.allEmployees.map(e => e.branch).filter(Boolean))]
    },

    filteredEmployees() {
      let list = this.allEmployees
      if (this.selectedBranch) {
        list = list.filter(e => e.branch === this.selectedBranch)
      }
      if (this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase()
        list = list.filter(e => (e.name || '').toLowerCase().includes(q)
          || (e.email || '').toLowerCase().includes(q)
          || (e.job_title || '').toLowerCase().includes(q))
      }
      return list
    },

    totalPages() {
      return Math.max(1, Math.ceil(this.filteredEmployees.length / this.perPage))
    },

    paginatedEmployees() {
      const start = (this.currentPage - 1) * this.perPage
      return this.filteredEmployees.slice(start, start + this.perPage)
    },
  },

  watch: {
    searchQuery() { this.currentPage = 1 },
    selectedBranch() { this.currentPage = 1 },
    filteredEmployees: {
      handler(list) {
        const maxPage = Math.max(1, Math.ceil(list.length / this.perPage))
        if (this.currentPage > maxPage) this.currentPage = maxPage
      },
    },
  },

  mounted() {
    this.fetchEmployees()
  },

  methods: {
    async fetchEmployees() {
      this.dataLoad = true
      try {
        const response = await axios.get(this.apiUrl)
        if (response.status === 200) {
          const responseData = decryptData(response.data.payload)
          const raw = responseData?.data[Object.keys(responseData?.data)[0]] || []
          this.allEmployees = raw
          this.buildBranchColorMap(raw)
        }
      } catch {
        this.$swal('Error!', 'Failed to load employees.', 'error')
      } finally {
        this.dataLoad = false
      }
    },

    buildBranchColorMap(employees) {
      const map = {}
      let idx = 0
      employees.forEach(e => {
        if (e.branch && !map[e.branch]) {
          map[e.branch] = idx % BRANCH_COLORS.length
          idx += 1
        }
      })
      this.branchColorMap = map
    },

    getBranchColor(branch) {
      const idx = this.branchColorMap[branch] ?? 0
      return BRANCH_COLORS[idx]
    },

    getBranchGradient(branch) {
      const idx = this.branchColorMap[branch] ?? 0
      return BRANCH_GRADIENTS[idx]
    },

    isUrl(str) {
      return str && (str.startsWith('http://') || str.startsWith('https://'))
    },

    handleImgError(e) {
      e.target.src = this.defaultAvatar
    },

    clearFilters() {
      this.searchQuery = ''
      this.selectedBranch = ''
    },

    /** Same as SweetAlert “Approve” confirm */
    goToApproveEmployee(id, ofice) {
      this.$router.push({
        name: 'approval-employee',
        params: { id },
        query: { ofice },
      })
    },

    /** Same as SweetAlert “Disapprove” (cancel) */
    disapproveEmployee(id) {
      axios
        .post(`/employees/${id}`, { approve: 0 })
        .then(() => {
          this.$swal('Disapproved!', 'Employee disapproved successfully.', 'warning')
          this.fetchEmployees()
        })
        .catch(() => {
          this.$swal('Error!', 'There was an issue with the disapproval.', 'error')
        })
    },
  },
}
</script>

<style scoped src="./EmployeesPage.css"></style>
